import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { TenantDateField } from '../../../components/TenantDateField';
import { UploadFileRounded } from '@mui/icons-material';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { tuitionApi } from '../../../lib/api/tuition.api';
import { useTenantStore } from '../../../stores/tenant.store';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  type StorageReference,
} from 'firebase/storage';
import { storage } from '../../../lib/firebase';
import { errorText } from '../../../lib/error-text';
import { formatMoney } from '../../../lib/i18n-format';

interface Installment {
  id: string;
  label: string;
  dueDate: string;
  amount: number;
  type: 'enrollment' | 'monthly';
  /** What's left to pay, already net of receipts awaiting verification. */
  balance: number;
  /** Allocated by receipts still in review — reserved, but not yet paid. */
  pendingAmount: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  tuitionPlanId: string;
  studentId: string;
  currency: string;
  installments: Installment[];
}

export function SubmitPaymentDialog({
  open,
  onClose,
  onSuccess,
  tuitionPlanId,
  studentId,
  currency,
  installments,
}: Props) {
  const { t } = useTranslation(['sis', 'common']);
  const { currentTenantId } = useTenantStore();

  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0] ?? '');
  const [method, setMethod] = useState<'transfer' | 'cash' | 'check' | 'online'>('transfer');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [selectedInstallments, setSelectedInstallments] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setAmount('');
    setReference('');
    setNotes('');
    setReceiptFile(null);
    setSelectedInstallments(new Set());
    setError(null);
    onClose();
  };

  const toggleInstallment = (id: string) => {
    setSelectedInstallments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      // Auto-fill amount with sum of selected installments' balances
      const total = installments
        .filter((i) => next.has(i.id))
        .reduce((sum, i) => sum + i.balance, 0);
      setAmount(total > 0 ? String(total) : '');
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!currentTenantId) return;

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError(t('tuition.errors.invalidAmount'));
      return;
    }
    if (!reference.trim()) {
      setError(t('tuition.errors.referenceRequired'));
      return;
    }
    if (selectedInstallments.size === 0) {
      setError(t('tuition.errors.selectInstallment'));
      return;
    }

    // For non-cash payments, a receipt (comprobante) is mandatory
    if (method !== 'cash' && !receiptFile) {
      setError(
        'Se requiere adjuntar el comprobante de pago (transferencia, cheque u online). Para pagos en efectivo el comprobante es opcional.',
      );
      return;
    }

    setSubmitting(true);
    setError(null);

    // Held so a rejected submit can take its receipt back out of Storage. The
    // upload has to happen first (the callable wants the URL), and the server now
    // rejects far more often than it used to — an over-allocated cuota, a repeated
    // reference — so without this every rejection would leave a file behind.
    let uploadedReceiptRef: StorageReference | null = null;

    try {
      let receiptUrl: string | undefined;
      if (receiptFile) {
        const storageRef = ref(
          storage,
          `tenants/${currentTenantId}/receipts/${studentId}/${Date.now()}_${receiptFile.name}`,
        );
        await uploadBytes(storageRef, receiptFile);
        uploadedReceiptRef = storageRef;
        receiptUrl = await getDownloadURL(storageRef);
      }

      // Distribute amount proportionally among selected installments
      const selectedList = installments.filter((i) => selectedInstallments.has(i.id));
      const totalBalance = selectedList.reduce((s, i) => s + i.balance, 0);
      const allocations = selectedList.map((i) => ({
        installmentId: i.id,
        amount:
          totalBalance > 0
            ? Math.round((i.balance / totalBalance) * numAmount)
            : Math.round(numAmount / selectedList.length),
      }));

      await tuitionApi.submitPaymentReceipt({
        tenantId: currentTenantId,
        tuitionPlanId,
        studentId,
        amount: numAmount,
        paymentDate,
        method,
        reference: reference.trim(),
        ...(receiptUrl ? { receiptUrl } : {}),
        allocations,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });

      // Committed: the payment owns the file now.
      uploadedReceiptRef = null;
      onSuccess?.();
      handleClose();
    } catch (e: any) {
      if (uploadedReceiptRef) {
        // Best-effort, and deliberately silent: the user has to see why the
        // payment was rejected, not a second error about cleaning up after it.
        deleteObject(uploadedReceiptRef).catch(() => {});
      }
      setError(errorText(e, t('tuition.errors.submitFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n: number) => formatMoney(n, currency);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('tuition.registerPayment')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Installment selection */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              {t('tuition.selectInstallments')} *
            </Typography>
            <Stack spacing={0.5}>
              {installments.map((ins) => {
                const isSettled = ins.balance <= 0;
                // Nothing left to pay, but the reason matters: money received
                // reads differently from a receipt still waiting on an admin.
                const isInReview = isSettled && ins.pendingAmount > 0;
                return (
                  <FormControlLabel
                    key={ins.id}
                    disabled={isSettled}
                    control={
                      <Checkbox
                        checked={selectedInstallments.has(ins.id)}
                        onChange={() => toggleInstallment(ins.id)}
                        size="small"
                      />
                    }
                    label={
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        width="100%"
                        sx={{ opacity: isSettled ? 0.5 : 1 }}
                      >
                        <Typography variant="body2">
                          {ins.label}{' '}
                          {isInReview
                            ? t('sis:tuition.installmentInReview')
                            : isSettled
                              ? t('sis:tuition.installmentPaid')
                              : ''}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={isSettled ? 'text.secondary' : 'warning.main'}
                        >
                          {fmt(isInReview ? ins.pendingAmount : ins.balance)}
                        </Typography>
                      </Stack>
                    }
                    sx={{ mr: 0, '& .MuiFormControlLabel-label': { width: '100%' } }}
                  />
                );
              })}
            </Stack>
          </Box>

          <Divider />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('sis:tuition.amount')}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              fullWidth
              inputProps={{ min: 0, step: 100 }}
              helperText={
                selectedInstallments.size > 0
                  ? `${currency} · ${t('sis:tuition.suggestedAmount')}: ${fmt(installments.filter((i) => selectedInstallments.has(i.id)).reduce((s, i) => s + i.balance, 0))}`
                  : currency
              }
            />
            <TenantDateField
              label={t('tuition.paymentDate')}
              value={paymentDate}
              onChange={setPaymentDate}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label={t('tuition.method')}
              value={method}
              onChange={(e) => setMethod(e.target.value as any)}
              fullWidth
            >
              <MenuItem value="transfer">{t('tuition.methods.transfer')}</MenuItem>
              <MenuItem value="cash">{t('tuition.methods.cash')}</MenuItem>
              <MenuItem value="check">{t('tuition.methods.check')}</MenuItem>
              <MenuItem value="online">{t('tuition.methods.online')}</MenuItem>
            </TextField>
            <TextField
              label={t('tuition.reference')}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              fullWidth
              placeholder="N° operación / transferencia"
            />
          </Stack>

          <TextField
            label={t('tuition.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />

          {/* Receipt upload */}
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileRounded />}
              onClick={() => fileInputRef.current?.click()}
              size="small"
            >
              {receiptFile ? receiptFile.name : t('tuition.uploadReceipt')}
            </Button>
            {receiptFile && (
              <Typography variant="caption" color="text.secondary" ml={1}>
                {(receiptFile.size / 1024).toFixed(0)} KB
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('common:cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          {t('tuition.submitPayment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
